<template>
  <div class="sm:px-12.5">
    <div
      class="flex flex-col xl:flex-row items-center 1.5xl:gap-25 xl:gap-20 lg:gap-14 gap-10 mb-7.5"
    >
      <div>
        <div class="1.5xl:pr-25 mb-6"><h2 v-i18n="landingPage">CHOOSE_YOUR_PLAN_TITLE</h2></div>

        <div class="text-justify"><p1 v-i18n="landingPage">CHOOSE_YOUR_PLAN_DISCRETION</p1></div>
      </div>
      <div class="flex flex-col md:flex-row gap-7.5 md:gap-2 1.5md:gap-7.5">
        <div
          class="w-80 md:w-76 1.5md:w-80 h-[332px] bg-primary-purple-50 p-7.5 md:p-3 1.5md:p-7.5 flex flex-col items-center rounded-19 justify-between"
        >
          <h3 class="mb-2.5">Free</h3>
          <p2>Up to 50 students for lifetime.</p2>
          <p class="text-6xl leading-[72px] font-medium">$0</p>
          <button
            class="lg:text-lg py-4 md:text-sm w-full rounded-lg bg-primary-purple-700 text-white font-medium flex items-center gap-5 justify-center"
            @click="redirectToSignup"
          >
            <span>Get Started</span>
            <span><Icon icon="arrow-right-up" /></span>
          </button>
        </div>
        <div
          class="relative w-80 md:w-76 1.5md:w-80 h-[332px] bg-secondary-orange-50 p-7.5 md:p-3 1.5md:p-7.5 flex flex-col items-center rounded-19 justify-between"
        >
          <div
            v-if="discountRate"
            class="ribbon w-[130px] h-[130px] overflow-hidden absolute -top-2.5 -right-2.5"
          >
            <span
              class="ribbon__content absolute left-[-25px] top-[30px] rotate-45 block w-[225px] py-[5px] bg-[#6941c6] text-white text-center uppercase text-sm font-medium border-[2px] border-dotted border-white outline outline-[5px] outline-[#fddcab] shadow-md"
            >
              {{ discountRate }}
            </span>
          </div>
          <h3 class="mb-2.5">Standard</h3>

          <div class="flex justify-center items-center text-center text-primary-light-grey">
            <span class="lg:mr-2.5 md:mr-0.5 mr-5">Students</span>
            <div class="bg-white rounded-lg border border-secondary-orange-200 w-[150px] px-3.5">
              <UiSingleSelect
                v-model="studentCount"
                :hide-title="true"
                :options="studentOptions"
                placeholder="Select"
                social-media="h-9"
                :hide-un-select-icon="true"
                :border-update="true"
                drop-down-container="top-10 w-full"
                @change="changePricingPlan"
              />
            </div>
          </div>

          <p v-if="monthlyPackage" class="text-6xl leading-[72px] font-medium">
            {{ monthlyPackage }}
            <span class="text-base -ml-3 lg:-ml-5">{{ duration }}</span>
          </p>

          <router-link v-if="!monthlyPackage" to="/contact-us">
            <h4 class="text-primary-purple-600 font-bold underline h-[72px]">Contact Us</h4>
          </router-link>

          <button
            class="lg:text-lg md:text-sm w-full rounded-lg bg-secondary-orange-500 text-white font-medium py-4 flex items-center gap-5 justify-center"
            @click="redirectToSignup"
          >
            <span>Get Started</span>
            <span><Icon icon="arrow-right-up" /></span>
          </button>
        </div>
      </div>
    </div>

    <div>
      <div class="overflow-x-auto rounded-[9px] border border-primary-purple-100">
        <table class="min-w-full bg-white divide-y">
          <thead>
            <tr class="divide-x">
              <th
                v-for="(header, index) in tableHeaders"
                :key="index"
                class="px-2 lg:px-6 py-4 text-left"
              >
                <p4 class="text-primary-gray-500">{{ header }}</p4>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y">
            <tr v-for="(option, idx) in options" :key="idx" class="divide-x">
              <td class="p-2 lg:p-6">
                <div class="flex py-0.5 gap-2 items-center">
                  <TooltipIcon :term="option.description" height="20" width="20" />
                  <p3>
                    {{ $t(`landingPage.${option.title}`) }}
                    <span v-if="option.link" class="text-primary-purple-600 font-bold underline">
                      <a :href="option.href" target="blank">{{ option.link }}</a>
                    </span>
                  </p3>
                </div>
              </td>
              <td class="text-center p-2 lg:p-6 xl:w-84">
                <input
                  :id="`Free${idx + 1}`"
                  type="checkbox"
                  :class="option.free"
                  class="hidden w-3.5 h-3.5"
                />
                <label :for="`Free${idx + 1}`" class="w-3.5 h-3.5"></label>
              </td>
              <td class="text-center p-2 lg:p-6 xl:w-84">
                <p3 v-if="option.coming">{{ option.coming }}</p3>
                <input
                  v-else
                  :id="`Standard${idx + 1}`"
                  type="checkbox"
                  class="hidden w-3.5 h-3.5"
                  :class="option.standard"
                />
                <label :for="`Standard${idx + 1}`" class="w-3.5 h-3.5"></label>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import TooltipIcon from '@/src/components/misc/TooltipIcon.vue'
import Icon from '@src/components/icons/icon.vue'
import UiSingleSelect from '@components/misc/UiSingleSelect.vue'

export default {
  name: 'PricingPackage',
  components: {
    TooltipIcon,
    Icon,
    UiSingleSelect,
  },
  data() {
    return {
      studentCount: 'LESS_THAN_100',
      landingPage: 'landingPage',
      monthlyPackage: '',
      discountRate: '',
      duration: 'per month',
      tableHeaders: ['Features Name', 'Free', 'Standard'],
      studentOptions: [
        'LESS_THAN_100',
        'LESS_THAN_200',
        'LESS_THAN_300',
        'LESS_THAN_500',
        'LESS_THAN_1000',
        'GREATER_THAN_1000',
      ],
      options: [
        {
          title: 'PACKAGE_PLAN_TITLE1',
          description: 'PACKAGE_PLAN_DESCRIPTION1',
          free: 'check_box_cancel',
          standard: 'check_box_ok',
        },
        {
          title: 'PACKAGE_PLAN_TITLE2',
          description: 'PACKAGE_PLAN_DESCRIPTION2',
          free: 'check_box_cancel',
          standard: 'check_box_ok',
        },
        {
          title: 'PACKAGE_PLAN_TITLE3',
          description: 'PACKAGE_PLAN_DESCRIPTION3',
          free: 'check_box_cancel',
          standard: 'check_box_ok',
        },
        {
          title: 'PACKAGE_PLAN_TITLE4',
          description: 'PACKAGE_PLAN_DESCRIPTION4',
          free: 'check_box_cancel',
          standard: 'check_box_ok',
        },
        {
          title: 'PACKAGE_PLAN_TITLE5',
          description: 'PACKAGE_PLAN_DESCRIPTION5',
          free: 'check_box_cancel',
          standard: 'check_box_ok',
        },
        {
          title: 'PACKAGE_PLAN_TITLE6',
          description: 'PACKAGE_PLAN_DESCRIPTION6',
          free: 'check_box_ok',
          standard: 'check_box_ok',
        },
        {
          title: 'PACKAGE_PLAN_TITLE7',
          description: 'PACKAGE_PLAN_DESCRIPTION7',
          free: 'check_box_ok',
          standard: 'check_box_ok',
        },
        {
          title: 'PACKAGE_PLAN_TITLE8',
          description: 'PACKAGE_PLAN_DESCRIPTION8',
          free: 'check_box_ok',
          standard: 'check_box_ok',
        },
        {
          title: 'PACKAGE_PLAN_TITLE9',
          description: 'PACKAGE_PLAN_DESCRIPTION9',
          free: 'check_box_ok',
          standard: 'check_box_ok',
        },
        {
          title: 'PACKAGE_PLAN_TITLE10',
          description: 'PACKAGE_PLAN_DESCRIPTION10',
          free: 'check_box_ok',
          standard: 'check_box_ok',
        },
        {
          title: 'PACKAGE_PLAN_TITLE11',
          description: 'PACKAGE_PLAN_DESCRIPTION11',
          free: 'check_box_ok',
          standard: 'check_box_ok',
        },
        {
          title: 'PACKAGE_PLAN_TITLE12',
          description: 'PACKAGE_PLAN_DESCRIPTION11',
          free: 'check_box_cancel',
          coming: 'Coming in 2025',
          link: 'Canvas LMS',
          href: 'https://www.instructure.com/canvas',
        },
        {
          title: 'PACKAGE_PLAN_TITLE13',
          description: 'PACKAGE_PLAN_DESCRIPTION11',
          free: 'check_box_cancel',
          coming: 'Coming in 2025',
          link: 'Moodle LMS',
          href: 'https://moodle.org/',
        },
      ],
      packages: [
        {
          package: '$100/',
          name: 'LESS_THAN_100',
          duration: 'per month',
          discountRate: '',
        },
        { package: '$190/', name: 'LESS_THAN_200', duration: 'per month', discountRate: '5% Off' },
        { package: '$270/', name: 'LESS_THAN_300', duration: 'per month', discountRate: '10% Off' },
        { package: '$375/', name: 'LESS_THAN_500', duration: 'per month', discountRate: '25% Off' },
        {
          package: '$600/',
          name: 'LESS_THAN_1000',
          duration: 'per month',
          discountRate: '40% Off',
        },
        { package: '', name: 'GREATER_THAN_1000', duration: 'per month', discountRate: '50% Off' },
      ],
    }
  },
  created() {
    this.changePricingPlan(this.studentCount)
  },
  methods: {
    redirectToSignup() {
      this.$router.push({ name: 'signup' })
    },
    changePricingPlan(value) {
      const selectedStudent = this.packages.find((pkg) => pkg.name === value)
      if (selectedStudent) {
        this.monthlyPackage = selectedStudent.package
        this.duration = selectedStudent.duration
        this.discountRate = selectedStudent.discountRate
      } else {
        this.monthlyPackage = ''
      }
    },
  },
}
</script>

<style scoped lang="scss">
.check_box_cancel + label {
  background: url('@src/assets/images/Unchecked.svg') no-repeat;
  background-size: cover;
  display: inline-block;
  padding: 0px;
}
.check_box_ok + label {
  background: url('@src/assets/images/checked.svg') no-repeat;
  background-size: cover;
  display: inline-block;
  padding: 0 0 0 0px;
}
.ribbon::before,
.ribbon::after {
  content: '';
  position: absolute;
  z-index: -1;
  border-style: solid;
  border-width: 5px;
}

.ribbon::before {
  top: 0px;
  left: 10px;
  border-top-color: transparent;
  border-right-color: transparent;
  border-color: #feb273;
}

.ribbon::after {
  bottom: 10px;
  right: 0;
  border-top-color: transparent;
  border-right-color: transparent;
  border-color: #feb273;
}
</style>
